/**
 * 产品一览表 (Product Overview) — a grid view of the FM ``product_photo``
 * layout. Photos are downloaded during sync and re-hosted on R2, so the
 * page renders instantly without needing FM session credentials.
 *
 * 2026-02-27 · Initial implementation. Filters mirror the FM schema:
 * category / buyer / photo_type + free-text search + date range.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "sonner";
import { Search, X, Loader2, Package as PackageIcon, ImagePlus, ChevronDown, PackagePlus, FilePlus2, Tag, LayoutGrid, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useQuote } from "@/context/QuoteContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ImportPhotosDialog } from "@/components/products/ImportPhotosDialog";
import { ProductDetailDialog } from "@/components/products/ProductDetailDialog";
import { AddToSampleListDialog } from "@/components/products/AddToSampleListDialog";
import { QuotePicker } from "@/components/QuotePicker";
import { TagPickerDialog } from "@/components/TagPickerDialog";
import { MobileFilterSheet } from "@/components/MobileFilterSheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function ProductOverview() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // 2026-10-12 · 每页显示数量下拉（记在 localStorage，默认 60 保持原行为相近 = 50 更常见选项之一）
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem("quotesys_po_page_size") || "", 10);
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 50;
  });
  const changePageSize = (n) => {
    setPageSize(n);
    localStorage.setItem("quotesys_po_page_size", String(n));
    setPage(1);
  };
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // 2026-06 · date_from/date_to = FM「日期」(列表里显示的那一列) 区间筛选。
  // 之前用的是 created_from/created_to（建立记录时间），而且 fetchRows 压根没把
  // 它们发给后端 —— 所以筛选看起来跟列表日期"对不上"(user report 2026-06)。
  const [filters, setFilters] = useState({ category: "", buyer: "", photo_type: "", date_from: "", date_to: "" });
  const [options, setOptions] = useState({ category: [], buyer: [], photo_type: [] });
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();
  // 2026-06 · 产品经理 (production_manager) has read-only visibility here.
  const canImport = user?.role !== "production_manager";
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  // 2026-06 · 寄样管理 — multi-select + Action menu
  const [picked, setPicked] = useState(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  // 2026-10-12 · 批量操作：加入报价单 / 打标签 / 删除 / 导出 PDF
  const { addToQuote, activeQuote } = useQuote();
  const [quotePickerOpen, setQuotePickerOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const togglePick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // 2026-10-12 · 从当前列表中根据 picked ids 取 item_numbers（给 /tags/batch
  // 和 /quotes/{id}/items 用；对应 FM product_photo 行的 item_number 字段）
  const selectedItemNumbers = useMemo(
    () => rows.filter((r) => picked.has(r.id) && r.item_number).map((r) => r.item_number),
    [rows, picked]
  );

  // Called when the detail dialog updates a row (e.g. AI 抠图 replaced the
  // photo_url) — patch the local list so the grid reflects it instantly.
  const patchRow = useCallback((updated) => {
    if (!updated?.id) return;
    setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    setSelectedRow(updated);
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      for (const k of ["category", "buyer", "photo_type", "date_from", "date_to"]) {
        if (filters[k]) params[k] = filters[k];
      }
      const { data } = await api.get("/product-overview", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("po.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filters, t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Load filter options once — the distinct-query is cheap compared to render.
  useEffect(() => {
    api.get("/product-overview/meta/options")
      .then(({ data }) => setOptions(data || {}))
      .catch(() => {});
  }, []);

  const clearFilters = () => {
    setSearch("");
    setFilters({ category: "", buyer: "", photo_type: "", date_from: "", date_to: "" });
    setPage(1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const hasFilters = search || filters.category || filters.buyer || filters.photo_type
    || filters.date_from || filters.date_to;

  // 2026-10-12 · 批量加入报价单：没选报价单先弹选择器，再把所有 item_numbers 挨个 POST。
  const batchAddToQuote = async (quote) => {
    const items = selectedItemNumbers;
    if (!items.length) { toast.error(t("po.batch.needItemNumber") || "所选记录没有产品编号，无法加入报价单"); return; }
    const target = quote || activeQuote;
    if (!target) { setQuotePickerOpen(true); return; }
    setBatchBusy(true);
    try {
      let ok = 0, fail = 0;
      for (const inum of items) {
        try { await addToQuote(inum, quote); ok++; }
        catch { fail++; }
      }
      toast.success(`${t("po.batch.addToQuoteDone") || "已加入报价单"}: ${ok}${fail ? ` · ${t("po.batch.failed") || "失败"}: ${fail}` : ""}`);
      setPicked(new Set());
    } finally {
      setBatchBusy(false);
    }
  };
  const handlePickQuote = async (quote) => {
    setQuotePickerOpen(false);
    await batchAddToQuote(quote);
  };

  // 2026-10-12 · 批量打标签 — 复用 /tags/batch，按 item_number 匹配产品
  const applyBatchTag = async (name) => {
    const items = selectedItemNumbers;
    if (!items.length || !name) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/tags/batch", { item_numbers: items, tag_name: name, action: "add" });
      toast.success(`${t("products.tagApplied") || "已应用标签"}: ${data.updated ?? items.length}`);
      setTagDialogOpen(false);
      setPicked(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBatchBusy(false);
    }
  };

  // 2026-10-12 · 批量导出 Grid PDF —— 复用 /products/grid-pdf
  const batchExportGridPdf = async () => {
    const items = selectedItemNumbers;
    if (!items.length) { toast.error(t("po.batch.needItemNumber") || "所选记录没有产品编号，无法导出"); return; }
    setBatchBusy(true);
    try {
      const { data } = await api.post(
        "/products/grid-pdf",
        { item_numbers: items, title: t("products.gridPdfTitle") || "产品图册" },
        { responseType: "blob", timeout: 120000 },
      );
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    } finally {
      setBatchBusy(false);
    }
  };

  // 2026-10-12 · 批量删除 ProductOverview 记录（admin only，循环 DELETE）
  const batchDelete = async () => {
    const ids = Array.from(picked);
    if (!ids.length) return;
    setBatchBusy(true);
    try {
      let ok = 0, fail = 0;
      for (const id of ids) {
        try { await api.delete(`/product-overview/${id}`); ok++; }
        catch { fail++; }
      }
      toast.success(`${t("products.deleted") || "已删除"}: ${ok}${fail ? ` · ${t("po.batch.failed") || "失败"}: ${fail}` : ""}`);
      setDeleteDialogOpen(false);
      setPicked(new Set());
      fetchRows();
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-[1600px] mx-auto" data-testid="product-overview-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold tracking-tight">{t("po.title") || "产品一览表"}</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {loading ? t("po.loading") : `${total.toLocaleString()} ${t("po.records") || "条记录"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canImport && (
            <Button size="sm" onClick={() => setImportOpen(true)} data-testid="po-open-import">
              <ImagePlus size={14} /> {t("po.import.button") || "导入产品图片"}
            </Button>
          )}
        </div>
      </header>

      {/* Filter bar — 2026-10-22 · 手机端收进底部抽屉，节省屏幕空间 */}
      <div className="flex flex-wrap items-center gap-2 bg-card border border-border rounded-md px-3 py-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder={t("po.searchHint") || "搜索产品编号 / 描述 / 采购员"}
            className="pl-8 h-8 text-sm"
            data-testid="po-search"
          />
        </div>

        {/* 2026-10-22 · 手机端筛选抽屉触发按钮 */}
        <MobileFilterSheet
          activeCount={[filters.category, filters.buyer, filters.photo_type, filters.date_from, filters.date_to].filter(Boolean).length}
          onReset={clearFilters}
          applyLabel={`${total} ${t("products.recordsFound")}`}
          testIdPrefix="po-m-filter"
          title={t("products.filters")}
        >
          {["category", "buyer", "photo_type"].map((f) => (
            <div key={f} className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t(`po.${f}`) || f}</p>
              <Select
                value={filters[f] || "__all__"}
                onValueChange={(v) => { setPage(1); setFilters((prev) => ({ ...prev, [f]: v === "__all__" ? "" : v })); }}
              >
                <SelectTrigger className="h-11 rounded-md text-sm w-full" data-testid={`po-m-filter-${f}`}>
                  <SelectValue placeholder={t(`po.${f}`) || f} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("po.all") || "全部"}</SelectItem>
                  {(options[f] || []).map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">{t("po.dateRange")}</p>
            <div className="flex items-center gap-1.5">
              <Input type="date" value={filters.date_from}
                onChange={(e) => { setPage(1); setFilters((p2) => ({ ...p2, date_from: e.target.value })); }}
                className="h-11 rounded-md font-mono text-xs" data-testid="po-m-filter-date-from" />
              <span className="text-muted-foreground text-xs">–</span>
              <Input type="date" value={filters.date_to}
                onChange={(e) => { setPage(1); setFilters((p2) => ({ ...p2, date_to: e.target.value })); }}
                className="h-11 rounded-md font-mono text-xs" data-testid="po-m-filter-date-to" />
            </div>
          </div>
        </MobileFilterSheet>

        {/* 桌面端筛选控件 —— 手机端走上面的抽屉 */}
        {["category", "buyer", "photo_type"].map((f) => (
          <Select
            key={f}
            value={filters[f] || "__all__"}
            onValueChange={(v) => { setPage(1); setFilters((prev) => ({ ...prev, [f]: v === "__all__" ? "" : v })); }}
          >
            <SelectTrigger className="hidden md:inline-flex h-8 w-[150px] text-xs" data-testid={`po-filter-${f}`}>
              <SelectValue placeholder={t(`po.${f}`) || f} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("po.all") || "全部"}</SelectItem>
              {(options[f] || []).map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        {/* 2026-06 · 按列表显示的「日期」列筛选（含头含尾） */}
        <div className="hidden md:flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">{t("po.dateRange")}</span>
          <Input type="date" value={filters.date_from}
            onChange={(e) => { setPage(1); setFilters((p2) => ({ ...p2, date_from: e.target.value })); }}
            className="h-8 w-[135px] text-xs" data-testid="po-filter-date-from" />
          <span className="text-muted-foreground">–</span>
          <Input type="date" value={filters.date_to}
            onChange={(e) => { setPage(1); setFilters((p2) => ({ ...p2, date_to: e.target.value })); }}
            className="h-8 w-[135px] text-xs" data-testid="po-filter-date-to" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="po-clear-filters" className="hidden md:inline-flex h-8">
            <X size={14} /> {t("po.clear") || "清空"}
          </Button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 size={16} className="animate-spin mr-2" /> {t("po.loading") || "加载中…"}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <PackageIcon size={40} className="opacity-40 mb-2" />
          <p className="text-sm">{t("po.empty") || "没有找到记录"}</p>
          {/* 2026-06 · 空结果时提示现有数据的日期范围（预览环境只有 2025-07） */}
          {(filters.date_from || filters.date_to) && options.date_min && (
            <p className="mt-1 text-xs font-mono" data-testid="po-date-range-hint">
              {t("po.dateRangeHint").replace("{from}", options.date_min).replace("{to}", options.date_max)}
            </p>
          )}
        </div>
      ) : (
        <>
          {picked.size > 0 && (
            <div className="sticky top-0 z-20 flex items-center gap-3 bg-card border border-brand/40 rounded-md px-3 py-2 shadow-sm"
              data-testid="po-selection-bar">
              <span className="text-xs font-mono" data-testid="po-selected-count">
                {t("sample.selected").replace("{n}", String(picked.size))}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={batchBusy} data-testid="po-action-btn">
                    Action <ChevronDown size={12} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setAddOpen(true)} data-testid="po-action-sample">
                    <PackagePlus size={13} className="mr-2" /> {t("sample.manage")}
                  </DropdownMenuItem>
                  {/* 2026-10-12 · 批量加入报价单 */}
                  <DropdownMenuItem onClick={() => batchAddToQuote()} data-testid="po-action-add-to-quote">
                    <FilePlus2 size={13} className="mr-2" /> {t("products.addToQuote") || "加入报价单"}
                  </DropdownMenuItem>
                  {/* 2026-10-12 · 批量打标签（走 /tags/batch） */}
                  <DropdownMenuItem onClick={() => setTagDialogOpen(true)} data-testid="po-action-set-tag">
                    <Tag size={13} className="mr-2" /> {t("products.setTag") || "设置标签"}
                  </DropdownMenuItem>
                  {/* 2026-10-12 · 批量导出 Grid PDF */}
                  <DropdownMenuItem onClick={batchExportGridPdf} data-testid="po-action-grid-pdf">
                    <LayoutGrid size={13} className="mr-2" /> {t("products.gridPdf") || "导出 Grid PDF"}
                  </DropdownMenuItem>
                  {/* 2026-10-12 · 批量删除（admin only） */}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} data-testid="po-action-delete"
                      className="text-destructive focus:text-destructive">
                      <Trash2 size={13} className="mr-2" /> {t("common.delete") || "删除"}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked(new Set())}
                data-testid="po-clear-selection">
                {t("market.clearSelection")}
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {rows.map((r) => (
              <ProductCard key={r.id} row={r} onClick={() => setSelectedRow(r)}
                selected={picked.has(r.id)} onToggle={() => togglePick(r.id)} />
            ))}
          </div>
        </>
      )}

      {/* Pagination + 每页数量 */}
      <div className="flex items-center justify-center gap-4 py-3">
        <div className="flex items-center gap-1.5" data-testid="po-page-size">
          <span className="text-[11px] text-muted-foreground">{t("products.perPage") || "每页"}</span>
          <Select value={String(pageSize)} onValueChange={(v) => changePageSize(parseInt(v, 10))}>
            <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="po-page-size-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`po-page-size-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {total > pageSize && (
          <div className="flex items-center gap-3" data-testid="po-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="po-prev">
              {t("po.prev") || "上一页"}
            </Button>
            <span className="text-xs font-mono text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="po-next">
              {t("po.next") || "下一页"}
            </Button>
          </div>
        )}
      </div>

      <ImportPhotosDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        options={options}
        onImported={() => { fetchRows(); /* refresh distinct values in case new buyer/category appeared */
                             api.get("/product-overview/meta/options").then(({ data }) => setOptions(data || {})).catch(() => {}); }}
      />

      <AddToSampleListDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        overviewIds={Array.from(picked)}
        onDone={() => setPicked(new Set())}
      />

      <ProductDetailDialog
        row={selectedRow}
        open={!!selectedRow}
        onOpenChange={(o) => { if (!o) setSelectedRow(null); }}
        onUpdated={patchRow}
      />

      {/* 2026-10-12 · 批量加入报价单：没选过报价单先弹选择器 */}
      <QuotePicker open={quotePickerOpen} onOpenChange={setQuotePickerOpen} onSelect={handlePickQuote} />

      {/* 2026-10-12 · 批量打标签 */}
      <TagPickerDialog
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        selectedCount={selectedItemNumbers.length}
        busy={batchBusy}
        onConfirm={applyBatchTag}
      />

      {/* 2026-10-12 · 批量删除确认（admin only） */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="po-batch-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">
              {t("common.delete")} ({picked.size})
            </DialogTitle>
            <DialogDescription>
              {(t("po.batch.deleteConfirm") || "确认删除所选的 {n} 条产品一览记录？该操作不可撤销。").replace("{n}", String(picked.size))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-md" data-testid="po-batch-delete-cancel">
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={batchDelete} disabled={batchBusy} className="rounded-md"
              data-testid="po-batch-delete-confirm">
              {batchBusy ? "..." : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * ProductCard — thumbnail + metadata + hover overlay showing full description
 * and buyer. Photo URL points to R2 (via ``/api/uploads/<uuid>.jpg``) so we
 * bypass FM's auth-locked streaming endpoint entirely.
 */
function ProductCard({ row, onClick, selected = false, onToggle }) {
  const [imgError, setImgError] = useState(false);
  const url = row.photo_url;
  const bare = url ? (url.startsWith("http") ? url : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${url}`) : "";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      className={`group border rounded-md overflow-hidden bg-card hover:shadow-md transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${selected ? "border-brand ring-2 ring-brand/30" : "border-border hover:border-brand/40"}`}
      data-testid={`po-card-${row.item_number || row.id}`}
    >
      <div className="aspect-square bg-muted/30 flex items-center justify-center relative">
        <span
          role="checkbox"
          aria-checked={selected}
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onToggle?.(); } }}
          className={`absolute top-1 left-1 z-10 w-5 h-5 rounded border flex items-center justify-center transition-opacity ${selected ? "bg-brand border-brand opacity-100" : "bg-background/90 border-border opacity-0 group-hover:opacity-100"}`}
          data-testid={`po-select-${row.item_number || row.id}`}
        >
          {selected && <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
        </span>
        {bare && !imgError ? (
          <img
            src={bare}
            alt={row.item_number || ""}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-contain"
          />
        ) : (
          <PackageIcon size={36} className="text-muted-foreground/40" />
        )}
        {row.photo_type && (
          <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/60 text-white text-[9px] font-semibold rounded-sm">
            {row.photo_type}
          </span>
        )}
      </div>
      <div className="px-2 py-1.5 text-[11px] leading-tight space-y-0.5">
        <p className="font-mono font-semibold truncate" title={row.item_number}>{row.item_number || "—"}</p>
        <p className="text-muted-foreground truncate" title={row.description}>{row.description || " "}</p>
        <p className="text-muted-foreground/70 flex items-center justify-between">
          <span className="truncate">{row.buyer || ""}</span>
          <span className="font-mono ml-1">{row.date || ""}</span>
        </p>
      </div>
    </div>
  );
}
